import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { zeroDigitPrecisionCurrencyCodes } from '../constants/CurrencyCodes';
import { AUTO_RENEWAL, COMMITTED, RENEWAL } from '../constants/commitmentTermTypes';
import { logPageActionInNewRelic } from './LoggingUtils';
import { UNSUPPORTED_CART_FEATURE } from '../constants/PageActions';
import { ADDED, UPDATED, REMOVED } from '../constants/ProductFeeChangeTypes';
import { CURRENCY_CHANGE, SKU_MIGRATION } from '../constants/PurchaseOrderTypes';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
export const groupProductsByApiName = products => products.reduce((result, product) => {
  result[product.productApiName] = result[product.productApiName] || [];
  result[product.productApiName].push(product);
  return result;
}, {}); // CART_API_V4: Remove usage of this function

export const getCurrencyCodeFromBilledTerm = billedTerm => {
  const {
    bundleProduct,
    recurringProducts
  } = billedTerm;
  const products = [];

  if (recurringProducts && recurringProducts.length > 0) {
    products.push(...recurringProducts);
  }

  if (bundleProduct && bundleProduct.componentProducts && bundleProduct.componentProducts.length > 0) {
    products.push(...bundleProduct.componentProducts);
  }

  const [{
    recurringProductFee: {
      currencyCode
    }
  }] = products;
  return currencyCode;
};
export const isRecurringProduct = product => product.recurringProductFee !== undefined && product.recurringProductFee !== null;
export const isOneTimeProduct = product => product.oneTimeProductFee !== undefined && product.oneTimeProductFee !== null;
export const isRemovedProduct = product => product.removedProductFee !== undefined && product.removedProductFee !== null;
export const extractProductFee = product => {
  if (isRecurringProduct(product)) {
    return product.recurringProductFee;
  }

  if (isOneTimeProduct(product)) {
    return product.oneTimeProductFee;
  }

  if (isRemovedProduct(product)) {
    return product.removedProductFee;
  }

  logPageActionInNewRelic(UNSUPPORTED_CART_FEATURE, {
    error: 'Cannot extract product fee from unrecognised product type'
  });
  throw new Error('Cannot extract product fee from unrecognised product type');
};
export const getRoundingPrecision = currencyCode => zeroDigitPrecisionCurrencyCodes.includes(currencyCode) ? 0 : 2;
export const getHasSplitTerms = subscriptionDetails => {
  const {
    subscriptionBilledTerms = []
  } = subscriptionDetails;
  const initialBilledTerm = subscriptionBilledTerms[0];
  const {
    billingPeriodLength
  } = initialBilledTerm || {};
  return subscriptionBilledTerms.some(billedTerm => billedTerm.billingPeriodLength !== billingPeriodLength);
};
export const extractSubscriptionDetailsInfo = subscriptionDetails => {
  if (!subscriptionDetails) {
    return {
      recurringProducts: [],
      hasSplitTerms: false
    };
  }

  const {
    subscriptionBilledTerms = []
  } = subscriptionDetails;
  const initialBilledTerm = subscriptionBilledTerms[0];
  const {
    recurringProducts = [],
    bundleProduct,
    billingPeriodLength
  } = initialBilledTerm || {};
  const {
    componentProducts = []
  } = bundleProduct || {};
  const termLength = subscriptionDetails.termLength;
  const hasSplitTerms = getHasSplitTerms(subscriptionDetails);
  const recurringOrBundleProducts = [...recurringProducts, ...componentProducts];
  return {
    recurringOrBundleProducts,
    billingPeriodLength,
    termLength,
    hasSplitTerms
  };
};
export const extractOrderDetailsInfo = orderDetails => {
  const {
    oneTimeProducts = [],
    removedProductsByEffectiveDate = [],
    subscriptionDetailsByTermType,
    today,
    billingOrderType,
    purchaseOrderType
  } = orderDetails;
  const initialSubscriptionDetails = subscriptionDetailsByTermType[COMMITTED][0] || subscriptionDetailsByTermType[AUTO_RENEWAL][0] || subscriptionDetailsByTermType[RENEWAL][0] || {};
  const {
    subscriptionBilledTerms = []
  } = initialSubscriptionDetails;
  const initialBilledTerm = subscriptionBilledTerms[0];
  const {
    recurringProducts = [],
    bundleProduct
  } = initialBilledTerm || {};
  const {
    componentProducts = []
  } = bundleProduct || {};
  const recurringOrBundleProducts = [...recurringProducts, ...componentProducts];
  const cartProducts = [...recurringOrBundleProducts, ...oneTimeProducts];
  const productFee = extractProductFee(cartProducts[0]);
  const {
    currencyCode
  } = productFee;
  return {
    billingOrderType,
    currencyCode,
    initialSubscriptionDetails,
    oneTimeProducts,
    removedProductsByEffectiveDate,
    purchaseOrderType,
    today
  };
};
export const isSubscriptionPaidMonthly = billingPeriodLength => {
  return billingPeriodLength === 1;
};
export const getTermsFromReviewDetails = reviewDetails => {
  if (!reviewDetails) {
    return {};
  }

  const {
    subscriptionDetailsByTermType
  } = reviewDetails;
  const committedTerms = subscriptionDetailsByTermType[COMMITTED];
  const renewalTerms = subscriptionDetailsByTermType[RENEWAL];
  const autoRenewalTerms = subscriptionDetailsByTermType[AUTO_RENEWAL];
  return {
    committedTerms,
    renewalTerms,
    autoRenewalTerms,
    committedAndRenewalTerms: [...committedTerms, ...renewalTerms]
  };
};
export const roundAmount = (amount, currencyCode) => {
  const roundingPrecision = getRoundingPrecision(currencyCode);
  const coefficient = Math.pow(10, roundingPrecision);

  if (amount < 0) {
    return -Math.round(-amount * coefficient) / coefficient;
  }

  return Math.round(amount * coefficient) / coefficient;
};
export const calculateEstimatedTotals = (recurringProducts, oneTimeProducts, currencyCode) => {
  let recurringPurchasesTotal = 0;
  let oneTimePurchasesTotal = 0;
  let discountTotal = 0;
  const groupedOneTimePurchaseTotals = {};
  recurringProducts.forEach(product => {
    const {
      discountedPricePerPayment,
      totalDiscountsAmountPerSinglePayment
    } = product.recurringProductFee;
    recurringPurchasesTotal += roundAmount(discountedPricePerPayment, currencyCode);
    discountTotal += roundAmount(totalDiscountsAmountPerSinglePayment, currencyCode);
  });
  oneTimeProducts.forEach(product => {
    const {
      discountedPricePerPayment,
      totalDiscountsAmountPerSinglePayment,
      numberOfPayments
    } = product.oneTimeProductFee;
    oneTimePurchasesTotal += roundAmount(discountedPricePerPayment, currencyCode);
    discountTotal += roundAmount(totalDiscountsAmountPerSinglePayment, currencyCode);
    groupedOneTimePurchaseTotals[numberOfPayments] = (groupedOneTimePurchaseTotals[numberOfPayments] || 0) + discountedPricePerPayment;
  });
  const estimatedTotal = roundAmount(recurringPurchasesTotal + oneTimePurchasesTotal, currencyCode);
  return {
    recurringPurchasesEstimatedTotal: recurringPurchasesTotal,
    oneTimePurchasesEstimatedTotal: oneTimePurchasesTotal,
    groupedOneTimePurchaseTotals,
    estimatedTotal,
    discountEstimatedTotal: discountTotal
  };
};
export const formatOrderDetailsDate = isoDate => I18n.moment(isoDate, 'YYYY-MM-DD').format('ll');
export const getProductChangeType = product => {
  if (isOneTimeProduct(product)) {
    return ADDED;
  }

  if (isRemovedProduct(product)) {
    return REMOVED;
  }

  if (isRecurringProduct(product)) {
    const {
      recurringProductFee: {
        recurringProductFeeChange
      }
    } = product;

    if (recurringProductFeeChange) {
      if (recurringProductFeeChange.changeType === ADDED) {
        return ADDED;
      }

      if (recurringProductFeeChange.changeType === UPDATED) {
        return UPDATED;
      }

      logPageActionInNewRelic(UNSUPPORTED_CART_FEATURE, {
        error: `Product contains recurringProductFeeChange with an unsupported changeType of type ${recurringProductFeeChange.changeType}`
      });
      throw new Error(`Product contains recurringProductFeeChange with an unsupported changeType of type ${recurringProductFeeChange.changeType}`);
    }
  }

  return null;
};
export const hasRecurrringProducts = cartDetails => {
  const {
    subscriptionDetailsByTermType
  } = cartDetails;
  const initialSubscriptionDetails = subscriptionDetailsByTermType[COMMITTED][0] || subscriptionDetailsByTermType[AUTO_RENEWAL][0] || subscriptionDetailsByTermType[RENEWAL][0] || {};
  const {
    subscriptionBilledTerms = []
  } = initialSubscriptionDetails;
  const initialBilledTerm = subscriptionBilledTerms[0] || {};
  const {
    recurringProducts = []
  } = initialBilledTerm;
  return recurringProducts.length > 0;
};
export const getGroupedOneTimeFees = oneTimeFees => oneTimeFees.reduce((group, oneTimeFee) => {
  group[oneTimeFee.numberOfPayments] = (group[oneTimeFee.numberOfPayments] || 0) + oneTimeFee.amount;
  return group;
}, {});
export const orderTypeWithoutTaxTotals = purchaseOrderType => purchaseOrderType === SKU_MIGRATION || purchaseOrderType === CURRENCY_CHANGE;
export const getFormattedDate = date => {
  return /*#__PURE__*/_jsx(FormattedDateTime, {
    value: I18n.moment.utc(date, 'YYYY-MM-DD'),
    format: "ll",
    type: "utc"
  });
};